import { Component, inject, Input, OnInit } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { select, Store } from '@ngrx/store'
import {
  CUSTOM_DEDUCTIONS,
  CUSTOM_EARNINGS,
  REIMBURSEMENTS
} from '../../../../store/state.constants'
import {
  addSalaryComponent,
  deleteSalaryComponent,
  editSalaryComponent
} from '../../../../store/salary-components/salary-components.actions'
import { SalaryComponent } from '../../../../store/salary-components/salary-components.state'
import { CommonModule, NgFor, NgIf, NgSwitch } from '@angular/common'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { Business } from '../../../../store/business/business.state'
import { filter, take, tap } from 'rxjs'
import { selectBusinesses } from '../../../../store/business/business.reducer'
import { loadBusinesses } from '../../../../store/business/business.actions'

@Component({
  selector: 'app-modals',
  imports: [NgFor, NgSwitch, NgIf, FormsModule, ReactiveFormsModule, CommonModule, CapitalizePipe],
  templateUrl: './modals.component.html',
  styleUrl: './modals.component.scss'
})
export class ModalsComponent implements OnInit {
  private store = inject(Store)
  private activeModal = inject(NgbActiveModal)
  private fb = inject(FormBuilder)
  @Input() salaryComponentName!: string
  form!: FormGroup

  //States
  customInput = ''
  showCustomInput = false
  currentStep = 1
  //TODO: auto fill selectedBusiness if user is an employee
  selectedBusiness = ''
  businessesList: Business[] = []
  operation = 'Add'
  itemData!: SalaryComponent

  ngOnInit() {
    this.store.dispatch(loadBusinesses({ params: null, page: 1, limit: 10 }))
    this.store
      .pipe(
        select(selectBusinesses),
        filter(b => !!b.length),
        take(1),
        tap(businesses => {
          this.businessesList = businesses
        })
      )
      .subscribe()
    this.initializeForm()
  }

  initializeForm() {
    switch (this.salaryComponentName) {
      case CUSTOM_EARNINGS:
        this.earningsForm()
        break
      case CUSTOM_DEDUCTIONS:
        this.deductionsForm()
        break
      case REIMBURSEMENTS:
        this.reimbursementsForm()
        break
      default:
        break
    }
  }

  earningsForm() {
    this.form = this.fb.group({
      belongsTo: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      componentType: [CUSTOM_EARNINGS],
      info: this.fb.group({
        deductionPlan: ['other'],
        payType: ['fixedPay'],
        calculationType: ['flatAmount'],
        paymentFrequency: ['monthly'],
        taxDeductionPreference: ['subsequentPayrolls'],
        taxable: [true],
        isInCTC: [true],
        isInFBP: [true],
        proRataBasis: [true],
        EPFContribution: [true],
        ESIContribution: [true],
        optional: [true]
      })
    })
  }

  deductionsForm() {
    this.form = this.fb.group({
      belongsTo: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      componentType: [CUSTOM_DEDUCTIONS],
      info: this.fb.group({
        deductionPlan: ['other'],
        payType: ['fixedPay'],
        calculationType: ['flatAmount'],
        paymentFrequency: ['monthly'],
        taxDeductionPreference: ['subsequentPayrolls'],
        taxable: [true],
        isInCTC: [true],
        isInFBP: [true],
        proRataBasis: [true],
        EPFContribution: [true],
        ESIContribution: [true],
        optional: [true]
      })
    })
  }

  reimbursementsForm() {
    this.form = this.fb.group({
      belongsTo: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      componentType: [REIMBURSEMENTS],
      info: this.fb.group({
        deductionPlan: ['other'],
        payType: ['fixedPay'],
        calculationType: ['flatAmount'],
        paymentFrequency: ['monthly'],
        taxDeductionPreference: ['subsequentPayrolls'],
        taxable: [true],
        isInCTC: [true],
        isInFBP: [true],
        proRataBasis: [true],
        EPFContribution: [true],
        ESIContribution: [true],
        optional: [true]
      })
    })
  }

  sendDataToParent() {
    switch (this.operation) {
      case 'Add':
        this.store.dispatch(addSalaryComponent({ salaryComponent: this.form.value }))
        break
      case 'Edit':
        this.store.dispatch(
          editSalaryComponent({ id: this.itemData.id, salaryComponent: this.form.value })
        )
        break
      case 'Delete':
        this.store.dispatch(
          deleteSalaryComponent({
            id: this.itemData.id,
            componentType: this.itemData.componentType
          })
        )
        break
      default:
        break
    }
    this.activeModal.close()
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  // Function to check if the current step is valid
  isStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!this.form.get('name')?.valid
      case 2:
        return !!this.form.get('info')?.valid
      default:
        return false
    }
  }

  goToNextStep(): void {
    if (this.isStepValid()) {
      this.currentStep++
    } else {
      alert('Please complete the form before proceeding!')
    }
  }

  stepChange(newStep: number): void {
    if (this.isStepValid()) {
      this.currentStep = newStep
    }
  }

  onOtherSelected() {
    this.showCustomInput = true
    this.customInput = '' // Clear custom input when switching to "other"
  }
  onSelectionChange() {
    this.showCustomInput = false // Hide the custom input
    this.customInput = '' // Clear custom input when switching away from "other"

    // Update form value to selected radio button value
    const selectedValue = this.form.get('info.paymentFrequency')?.value
    if (selectedValue !== 'other') {
      this.form.get('info.paymentFrequency')?.setValue(selectedValue)
    }
  }

  onClose(): void {
    this.activeModal.close()
  }
}
